import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Receipt, Sparkles, ChevronDown, ChevronUp, Clock, MapPin, Camera, CalendarClock, Inbox, X as XIcon, Columns2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { portalApi } from "@/lib/portalApi";
import PhotoLightbox from "@/components/PhotoLightbox";
import { nombreFotoBase } from "@/lib/fotoUtils";

const eur = (n) => (n ?? 0).toLocaleString("es-ES", { style: "currency", currency: "EUR" });

const ESTADO_FACTURA = {
  paid: { label: "Pagada", color: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" },
  pending: { label: "Pendiente", color: "bg-amber-500/20 text-amber-300 border-amber-500/40" },
  overdue: { label: "Vencida", color: "bg-red-500/20 text-red-300 border-red-500/40" },
  overpaid: { label: "Pagada", color: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" },
};

export function PortalFacturas() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    portalApi().get("/portal/facturas")
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Error cargando facturas"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="py-8 text-center text-zinc-500"><Loader2 className="w-5 h-5 animate-spin inline" /></div>;
  if (!data?.disponible) {
    return (
      <div className="text-center py-12 text-zinc-500" data-testid="facturas-no-disponible">
        <Receipt className="w-10 h-10 mx-auto opacity-40 mb-2" />
        <div className="text-sm">Aún no hay datos de facturación disponibles.</div>
        <div className="text-xs mt-1">Contacta con tu comercial DNF si crees que es un error.</div>
      </div>
    );
  }

  const k = data.kpis || {};
  return (
    <div data-testid="portal-facturas">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
        <KpiCard label="Facturado" value={eur(k.facturado)} tone="text-zinc-100" />
        <KpiCard label="Pagado" value={eur(k.pagado)} tone="text-emerald-300" border="border-emerald-500/30 bg-emerald-500/5" />
        <KpiCard label="Pendiente" value={eur(k.pendiente)} tone="text-amber-300" border="border-amber-500/30 bg-amber-500/5" />
        <KpiCard label="Vencido" value={eur(k.vencido)} tone="text-red-300" border="border-red-500/30 bg-red-500/5" />
      </div>
      <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl overflow-hidden">
        <div className="bg-zinc-950/60 border-b border-zinc-800 px-3 py-2 text-[11px] uppercase tracking-wider text-zinc-500 flex justify-between">
          <span>Facturas ({k.n_facturas || 0})</span>
          {data.actualizado_at && <span className="normal-case">Actualizado: {new Date(data.actualizado_at).toLocaleDateString("es-ES")}</span>}
        </div>
        {!data.facturas?.length ? (
          <div className="py-8 text-center text-zinc-500 text-sm">No hay facturas emitidas todavía.</div>
        ) : (
          <div className="divide-y divide-zinc-800/60">
            {data.facturas.map((f) => {
              const est = ESTADO_FACTURA[f.fd_state] || { label: f.fd_state, color: "bg-zinc-700/40 text-zinc-300 border-zinc-700" };
              return (
                <div key={f.fd_numero} className="px-3 py-2.5 flex items-center gap-3 flex-wrap"
                  data-testid={`factura-${f.fd_numero}`}>
                  <span className="font-mono text-xs text-amber-300 w-24 shrink-0">{f.fd_numero}</span>
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${est.color}`}>{est.label}</span>
                  <div className="flex-1 text-right">
                    <div className="text-sm font-semibold">{eur(f.fd_total)}</div>
                    {f.fd_pending > 0 && f.fd_state !== "paid" && (
                      <div className="text-[10px] text-amber-400">Pendiente: {eur(f.fd_pending)}</div>
                    )}
                  </div>
                  <div className="text-[11px] text-zinc-500 w-full sm:w-auto sm:text-right">
                    <div>Emitida: {f.fd_date ? new Date(f.fd_date).toLocaleDateString("es-ES") : "—"}</div>
                    {f.fd_due_date && <div>Vence: {new Date(f.fd_due_date).toLocaleDateString("es-ES")}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function KpiCard({ label, value, tone, border = "border-zinc-800 bg-zinc-900/60" }) {
  return (
    <div className={`border rounded-lg p-3 text-center ${border}`}>
      <div className={`text-lg font-bold ${tone}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 mt-1">{label}</div>
    </div>
  );
}

export function PortalResumenCards() {
  const [r, setR] = useState(null);
  useEffect(() => {
    portalApi().get("/portal/resumen").then(({ data }) => setR(data)).catch(() => {});
  }, []);
  if (!r) return null;
  const cards = [
    { label: "Campañas activas", value: r.activas, icon: Sparkles, tone: "text-amber-300" },
    { label: `Completadas ${new Date().getFullYear()}`, value: r.completadas_anio, icon: CalendarClock, tone: "text-emerald-300" },
    { label: "Espacios trabajados", value: r.espacios_distintos, icon: MapPin, tone: "text-cyan-300" },
    { label: "Fotos disponibles", value: r.fotos_totales, icon: Camera, tone: "text-violet-300" },
  ];
  if (r.tiempo_medio_dias != null) {
    cards.push({ label: "Tiempo medio (días)", value: r.tiempo_medio_dias, icon: Clock, tone: "text-zinc-200" });
  }
  if (r.solicitudes_pendientes > 0) {
    cards.push({ label: "Solicitudes en revisión", value: r.solicitudes_pendientes, icon: Inbox, tone: "text-amber-300" });
  }
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4" data-testid="portal-resumen-cards">
      {cards.map((c) => (
        <div key={c.label} className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-3">
          <c.icon className={`w-4 h-4 mb-1.5 ${c.tone} opacity-70`} />
          <div className={`text-xl font-bold ${c.tone}`}>{c.value}</div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mt-0.5">{c.label}</div>
        </div>
      ))}
    </div>
  );
}

export function PortalActividad() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    portalApi().get("/portal/actividad").then(({ data }) => {
      setData(data);
      if (data.novedades > 0) setOpen(true);
    }).catch(() => {});
  }, []);
  if (!data?.eventos?.length) return null;
  return (
    <div className="mb-3 bg-zinc-900/40 border border-zinc-800 rounded-lg overflow-hidden" data-testid="portal-actividad">
      <button onClick={() => setOpen(!open)} data-testid="portal-actividad-toggle"
        className="w-full px-3 py-2 flex items-center justify-between text-left hover:bg-zinc-900/60">
        <div className="flex items-center gap-2 text-xs">
          <Sparkles className="w-3.5 h-3.5 text-amber-400" />
          <span className="text-zinc-300 font-semibold">Actividad reciente</span>
          {data.novedades > 0 && (
            <span className="bg-amber-500 text-zinc-900 text-[10px] font-bold rounded-full px-1.5 py-0.5"
              data-testid="portal-novedades-badge">
              {data.novedades} {data.novedades === 1 ? "novedad" : "novedades"}
            </span>
          )}
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-zinc-500" />}
      </button>
      {open && (
        <div className="border-t border-zinc-800/60 divide-y divide-zinc-800/40 max-h-64 overflow-y-auto">
          {data.eventos.map((e, i) => (
            <div key={i} className="px-3 py-2 text-xs flex gap-2">
              <span className="text-zinc-600 shrink-0 w-16">{e.fecha ? new Date(e.fecha).toLocaleDateString("es-ES", { day: "2-digit", month: "short" }) : ""}</span>
              <div className="min-w-0">
                <span className="text-amber-400/80 font-mono mr-1.5">{e.codigo}</span>
                <span className="text-zinc-400">{e.descripcion}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const fmtFecha = (f) => (f ? new Date(f).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" }) : "—");

function MontajeCard({ m, seleccionable, seleccionado, onToggle, onOpen }) {
  const thumb = m.fotos?.[0]?.url;
  return (
    <button onClick={() => (seleccionable ? onToggle(m) : onOpen(m))}
      data-testid={`galeria-montaje-${m.linea_id}`}
      className={`text-left rounded-xl border overflow-hidden transition-colors ${seleccionado
        ? "border-amber-500 ring-1 ring-amber-500" : "border-zinc-800 hover:border-zinc-600"} bg-zinc-900/60`}>
      <div className="aspect-video bg-zinc-950 relative">
        {thumb ? (
          <img src={thumb} alt={m.peticion_nombre || ""} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-zinc-700"><Camera className="w-8 h-8" /></div>
        )}
        {m.n_fotos > 1 && (
          <span className="absolute bottom-1 right-1 bg-black/70 text-white text-[10px] rounded px-1.5 py-0.5">
            <Camera className="w-3 h-3 inline mr-0.5" />{m.n_fotos}
          </span>
        )}
        {seleccionable && (
          <span className={`absolute top-1 left-1 w-5 h-5 rounded-full border flex items-center justify-center text-[10px] font-bold ${seleccionado ? "bg-amber-500 border-amber-500 text-zinc-900" : "bg-black/50 border-zinc-500 text-transparent"}`}>✓</span>
        )}
      </div>
      <div className="p-2">
        {m.marca && <div className="text-[11px] text-violet-300 font-semibold truncate">{m.marca}</div>}
        <div className="text-xs text-zinc-300 truncate">{m.peticion_nombre}</div>
        <div className="text-[10px] text-zinc-500 mt-0.5 flex justify-between">
          <span className="font-mono text-amber-400/70">{m.codigo}</span>
          <span>{fmtFecha(m.fecha)}</span>
        </div>
      </div>
    </button>
  );
}

function MontajeFotos({ m, espacioNombre }) {
  const fotos = m.fotos || [];
  const [lbIdx, setLbIdx] = useState(null);
  const nameBase = nombreFotoBase(espacioNombre, m.marca);
  return (
    <div>
      <div className="text-xs mb-2">
        {m.marca && <span className="text-violet-300 font-semibold mr-2">{m.marca}</span>}
        <span className="text-zinc-300">{m.peticion_nombre}</span>
        <span className="text-zinc-500 ml-2">{fmtFecha(m.fecha)}</span>
      </div>
      <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1">
        {fotos.map((f, i) => (
          <button key={i} type="button" onClick={() => setLbIdx(i)}
            data-testid={`galeria-foto-${i}`}
            className="block w-full cursor-zoom-in">
            <img src={f.url} alt="" className="w-full rounded-lg border border-zinc-800 hover:border-amber-500/50 transition-colors" loading="lazy" />
          </button>
        ))}
        {!fotos.length && <div className="text-zinc-500 text-sm py-6 text-center">Sin fotos</div>}
      </div>
      <PhotoLightbox
        fotos={fotos}
        openIdx={lbIdx}
        onClose={() => setLbIdx(null)}
        title={m.peticion_nombre}
        nameForIdx={(i) => `${nameBase}_${String(i + 1).padStart(2, "0")}`}
      />
    </div>
  );
}

export function PortalEspacioGaleria({ espacio, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [marca, setMarca] = useState("todas");
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [comparando, setComparando] = useState(false);
  const [sel, setSel] = useState([]); // montajes seleccionados para comparar (max 2)
  const [abierto, setAbierto] = useState(null); // montaje abierto en detalle

  useEffect(() => {
    if (!espacio?.espacio_id) return;
    setLoading(true);
    portalApi().get(`/portal/espacios/${espacio.espacio_id}/montajes`)
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Error cargando galería"))
      .finally(() => setLoading(false));
  }, [espacio?.espacio_id]);

  const montajes = (data?.montajes || []).filter((m) => {
    if (marca !== "todas" && m.marca !== marca) return false;
    const f = (m.fecha || "").slice(0, 10);
    if (desde && f && f < desde) return false;
    if (hasta && f && f > hasta) return false;
    return true;
  });

  const toggleSel = (m) => {
    setSel((s) => {
      const k = m.parada_key || m.linea_id;
      if (s.find((x) => (x.parada_key || x.linea_id) === k)) return s.filter((x) => (x.parada_key || x.linea_id) !== k);
      if (s.length >= 2) return [s[1], m];
      return [...s, m];
    });
  };

  return (
    <Dialog open={!!espacio} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#0c0c10] border-zinc-800 text-zinc-100 max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="galeria-espacio-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <MapPin className="w-4 h-4 text-amber-400" />
            {data?.espacio?.nombre || espacio?.nombre}
            {data?.espacio?.ciudad && <span className="text-xs text-zinc-500 font-normal">{data.espacio.ciudad}</span>}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-10 text-center text-zinc-500"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
        ) : abierto ? (
          <div>
            <button onClick={() => setAbierto(null)} data-testid="galeria-volver-btn"
              className="text-xs text-amber-400 hover:underline mb-3">← Volver a la galería</button>
            <MontajeFotos m={abierto} espacioNombre={data?.espacio?.nombre} />
          </div>
        ) : sel.length === 2 && comparando ? (
          <div>
            <button onClick={() => { setSel([]); setComparando(false); }} data-testid="galeria-cerrar-comparar"
              className="text-xs text-amber-400 hover:underline mb-3">← Salir de la comparación</button>
            <div className="grid grid-cols-2 gap-3" data-testid="galeria-comparador">
              <MontajeFotos m={sel[0]} espacioNombre={data?.espacio?.nombre} />
              <MontajeFotos m={sel[1]} espacioNombre={data?.espacio?.nombre} />
            </div>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2 text-xs">
              {(data?.marcas || []).length > 0 && (
                <select value={marca} onChange={(e) => setMarca(e.target.value)}
                  data-testid="galeria-filtro-marca"
                  className="bg-zinc-900 border border-zinc-800 rounded px-2 py-1.5">
                  <option value="todas">Todas las marcas</option>
                  {data.marcas.map((mk) => <option key={mk} value={mk}>{mk}</option>)}
                </select>
              )}
              <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)}
                data-testid="galeria-filtro-desde"
                className="bg-zinc-900 border border-zinc-800 rounded px-2 py-1" />
              <span className="text-zinc-600">—</span>
              <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)}
                data-testid="galeria-filtro-hasta"
                className="bg-zinc-900 border border-zinc-800 rounded px-2 py-1" />
              <button onClick={() => { setComparando(!comparando); setSel([]); }}
                data-testid="galeria-comparar-btn"
                className={`ml-auto flex items-center gap-1 rounded px-2.5 py-1.5 border ${comparando
                  ? "border-amber-500 bg-amber-500/15 text-amber-300" : "border-zinc-700 text-zinc-400 hover:text-zinc-200"}`}>
                <Columns2 className="w-3.5 h-3.5" /> {comparando ? `Elige 2 (${sel.length}/2)` : "Comparar"}
              </button>
            </div>
            {!montajes.length ? (
              <div className="py-10 text-center text-zinc-500 text-sm">No hay montajes con fotos para estos filtros.</div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="galeria-grid">
                {montajes.map((m) => (
                  <MontajeCard key={m.parada_key || m.linea_id} m={m}
                    seleccionable={comparando}
                    seleccionado={!!sel.find((x) => (x.parada_key || x.linea_id) === (m.parada_key || m.linea_id))}
                    onToggle={toggleSel}
                    onOpen={setAbierto} />
                ))}
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
